import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Calculator, BarChart3, Play, AlertTriangle, FileText, Sun, Moon } from 'lucide-react';

// Import components
import InputForm from './components/InputForm';
import ResultsTable from './components/ResultsTable';
import Charts from './components/Charts';
import SimulationMode from './components/SimulationMode';
import RiskWarnings from './components/RiskWarnings';
import MetaTags, { META_CONFIGS } from './components/MetaTags';
import SchemaMarkup from './components/SchemaMarkup';

// Import utilities
import { calculateMartingaleProgression, generateRiskWarnings } from './utils/martingaleCalculator';

// Import styles
import './styles/App.css';

const DEFAULT_CONFIG = {
  baseBet: 10,
  bankroll: 1000,
  odds: '2.00',
  oddsType: 'decimal',
  maxRounds: 10,
  targetProfit: null,
  betMultiplier: 2,
  maxTableLimit: null
};

function App() {
  const [activeTab, setActiveTab] = useState('configure');
  const [calculationConfig, setCalculationConfig] = useState(DEFAULT_CONFIG);
  const [calculationResults, setCalculationResults] = useState(() => {
    try {
      return calculateMartingaleProgression(DEFAULT_CONFIG);
    } catch (e) {
      return null;
    }
  });
  const [isCalculating, setIsCalculating] = useState(false);
  const [risksAcknowledged, setRisksAcknowledged] = useState(false);
  const [expandedFaqs, setExpandedFaqs] = useState({});
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme) {
        return savedTheme === 'dark';
      }
    }
    return false; // Default to Light Mode
  });

  // Sync theme with document root
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    }
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(prev => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        localStorage.setItem('theme', next ? 'dark' : 'light');
      }
      return next;
    });
  };

  // Tab configuration
  const tabs = [
    {
      id: 'configure',
      label: 'Configure',
      icon: Calculator,
      description: 'Set up your Martingale strategy parameters'
    },
    {
      id: 'results',
      label: 'Results Table',
      icon: FileText,
      description: 'View detailed progression analysis',
      disabled: !calculationResults
    },
    {
      id: 'charts',
      label: 'Charts',
      icon: BarChart3,
      description: 'Visual analysis and insights',
      disabled: !calculationResults
    },
    {
      id: 'simulation',
      label: 'Simulation',
      icon: Play,
      description: 'Monte Carlo simulation',
      disabled: !calculationResults
    },
    {
      id: 'risks',
      label: 'Risk Analysis',
      icon: AlertTriangle,
      description: 'Risk assessment and warnings',
      disabled: !calculationResults
    }
  ];

  // Handle calculation request
  const handleCalculate = async (config) => {
    setIsCalculating(true);
    setCalculationConfig(config);
    
    try {
      // Small delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Calculate results
      const results = calculateMartingaleProgression(config);
      
      // Check for high-risk scenarios
      const warnings = generateRiskWarnings(config, results.statistics);
      const hasHighRiskWarnings = warnings.some(w => w.type === 'high-risk' || w.type === 'negative-ev');
      
      if (hasHighRiskWarnings && !risksAcknowledged) {
        setCalculationResults(results);
        setActiveTab('risks');
        toast.error('High-risk configuration detected. Please review warnings.');
      } else {
        setCalculationResults(results);
        setActiveTab('results');
        toast.success('Calculation completed successfully!');
      }
      
    } catch (error) {
      console.error('Calculation error:', error);
      toast.error('An error occurred during calculation. Please check your inputs.');
    } finally {
      setIsCalculating(false);
    }
  };

  // Handle risk acknowledgment
  const handleRiskAcknowledgment = (acknowledged) => {
    setRisksAcknowledged(acknowledged);
    
    if (acknowledged) {
      setActiveTab('results');
      toast.success('Risks acknowledged. Proceeding with analysis.');
    }
  };

  // Handle tab change
  const handleTabChange = (tabId) => {
    const tab = tabs.find(t => t.id === tabId);
    if (tab && !tab.disabled) {
      setActiveTab(tabId);
    }
  };

  // Handle export functionality
  const handleExport = () => {
    toast.success('Data exported to CSV successfully!');
  };

  // Handle copy functionality
  const handleCopy = () => {
    toast.success('Table data copied to clipboard!');
  };

  // Reset application state
  const handleReset = () => {
    const defaultRes = calculateMartingaleProgression(DEFAULT_CONFIG);
    setCalculationResults(defaultRes);
    setCalculationConfig(DEFAULT_CONFIG);
    setRisksAcknowledged(false);
    setActiveTab('configure');
    toast.success('Application reset successfully!');
  };

  // Toggle FAQ expansion
  const toggleFaq = (faqIndex) => {
    setExpandedFaqs(prev => ({
      ...prev,
      [faqIndex]: !prev[faqIndex]
    }));
  };

  // Get meta tags configuration based on active tab
  const getMetaConfig = () => {
    switch (activeTab) {
      case 'configure':
        return META_CONFIGS.HOME;
      case 'results':
        return META_CONFIGS.HOME;
      case 'charts':
        return META_CONFIGS.HOME;
      case 'simulation':
        return META_CONFIGS.SIMULATION;
      case 'risks':
        return META_CONFIGS.RISK_ANALYSIS;
      default:
        return META_CONFIGS.HOME;
    }
  };

  const metaConfig = getMetaConfig();

  return (
    <div className={`App ${darkMode ? 'dark-mode' : ''}`} data-theme={darkMode ? 'dark' : 'light'}>
      {/* Meta Tags for Dynamic Head Injection */}
      <MetaTags {...metaConfig} />
      
      {/* Schema.org JSON-LD Structured Data */}
      <SchemaMarkup />

      {/* Header */}
      <header className="app-header">
        <div className="header-content">
          <div className="header-top-bar">
            <span className="header-badge">🎡 Free Roulette & Betting Simulator</span>
            <button
              onClick={toggleDarkMode}
              className="theme-toggle-button"
              aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              type="button"
            >
              {darkMode ? (
                <>
                  <Sun size={17} className="theme-icon sun-icon" />
                  <span>Light Mode</span>
                </>
              ) : (
                <>
                  <Moon size={17} className="theme-icon moon-icon" />
                  <span>Dark Mode</span>
                </>
              )}
            </button>
          </div>
          <h1 className="header-title">
            <Calculator size={32} />
            <span className="header-title-text">
              Martingale Strategy Calculator – Roulette & Betting Simulator
            </span>
          </h1>
          <p className="header-subtitle">
            Free online Martingale betting calculator for roulette, blackjack, and casino betting. Calculate required bankroll, progression steps, loss probabilities, and test your betting strategy safely.
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="main-container">

        {/* Tab Navigation */}
        <nav className="tab-navigation" aria-label="Calculator Navigation">
          <div className="tab-list">
            {tabs.map((tab) => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`tab-button ${activeTab === tab.id ? 'active' : ''} ${tab.disabled ? 'disabled' : ''}`}
                  disabled={tab.disabled}
                  title={tab.description}
                  type="button"
                >
                  <IconComponent size={18} />
                  {tab.label}
                </button>
              );
            })}
          </div>
          
          {calculationResults && (
            <div className="tab-actions">
              <button onClick={handleReset} className="action-button secondary" type="button">
                Reset Calculator
              </button>
            </div>
          )}
        </nav>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === 'configure' && (
            <InputForm 
              onCalculate={handleCalculate}
              loading={isCalculating}
            />
          )}

          {activeTab === 'results' && calculationResults && (
            <ResultsTable 
              results={calculationResults}
              onExport={handleExport}
              onCopy={handleCopy}
            />
          )}

          {activeTab === 'charts' && calculationResults && (
            <Charts results={calculationResults} />
          )}

          {activeTab === 'simulation' && calculationResults && (
            <SimulationMode config={calculationConfig} />
          )}

          {activeTab === 'risks' && calculationResults && (
            <RiskWarnings 
              config={calculationConfig}
              statistics={calculationResults.statistics}
              onAcknowledge={handleRiskAcknowledgment}
            />
          )}
        </div>

        {/* Progress Indicator */}
        {calculationResults && (
          <div className="progress-indicator">
            <div className="progress-steps">
              <div className={`step ${activeTab === 'configure' ? 'active' : 'completed'}`}>
                <Calculator size={16} />
                <span>Configure</span>
              </div>
              <div className={`step ${activeTab === 'results' ? 'active' : calculationResults ? 'completed' : ''}`}>
                <FileText size={16} />
                <span>Results</span>
              </div>
              <div className={`step ${activeTab === 'charts' ? 'active' : ''}`}>
                <BarChart3 size={16} />
                <span>Charts</span>
              </div>
              <div className={`step ${activeTab === 'simulation' ? 'active' : ''}`}>
                <Play size={16} />
                <span>Simulation</span>
              </div>
              <div className={`step ${activeTab === 'risks' ? 'active' : ''}`}>
                <AlertTriangle size={16} />
                <span>Risk Analysis</span>
              </div>
            </div>
          </div>
        )}

        {/* Mid-Page Sponsor / Recommended Platform Card */}
        <section className="top-promotional-banner">
          <div className="promotional-content">
            <a href="https://stake.bet/?c=2uo9ldlS" target="_blank" rel="nofollow sponsored noopener noreferrer">
              <img
                src={`${process.env.PUBLIC_URL}/martigale strategy calculater.webp`}
                alt="Martingale Strategy Calculator - Stake Casino"
                className="top-promotional-image"
                loading="lazy"
                width="250"
                height="150"
              />
            </a>
            <div className="promotional-text-content">
              <h3>Try Your Strategies at Stake Casino</h3>
              <p>Practice Martingale and progressive betting strategies with instant payouts and cryptocurrency support.</p>
              <a href="https://stake.bet/?c=2uo9ldlS" target="_blank" rel="nofollow sponsored noopener noreferrer" className="promotional-button">
                Visit Stake Casino
              </a>
            </div>
          </div>
        </section>

        {/* Educational Content Section */}
        <section className="educational-content">
          <article className="content-section">
            <h2>How to Use the Martingale Strategy Calculator for Roulette & Betting</h2>
            <p>
              The <strong>Martingale strategy</strong> is one of the most famous betting systems in the world, primarily utilized in <strong>Roulette</strong> on even-money propositions (Red/Black, Odd/Even, 1-18/19-36), as well as Blackjack and Baccarat.
              Our <strong>Martingale betting calculator</strong> simulates progressive rounds with precision, calculating your cumulative stakes, required bankroll, table limits headroom, and consecutive loss probabilities in real-time.
            </p>
            
            <h3>Core Principles of the Martingale Betting System</h3>
            <p>
              In a standard Martingale progression, you double your bet size following each loss (1x, 2x, 4x, 8x, 16x, 32x, 64x, 128x...). When a win occurs, you recover all prior consecutive losses plus a profit equal to your original base bet.
            </p>
            <ul>
              <li><strong>Exponential Growth</strong>: Bet sizes grow exponentially (Bet<sub>n</sub> = BaseBet &times; 2<sup>n-1</sup>), requiring a substantial bankroll to survive long loss streaks.</li>
              <li><strong>Table Limits Hazard</strong>: Casino maximum bet limits prevent indefinite doubling, making long-term theoretical certainty impossible.</li>
              <li><strong>House Edge Drag</strong>: In European Roulette (single zero, 2.70% house edge) or American Roulette (double zero, 5.26% house edge), the negative mathematical expectation persists over large sample sizes.</li>
            </ul>

            <h3>Benefits of Using Our Martingale Roulette Calculator</h3>
            <ul>
              <li><strong>Bankroll Risk Management</strong>: Determine exactly how many consecutive losses your bankroll can sustain before exhaustion.</li>
              <li><strong>Grand Martingale Testing</strong>: Simulate aggressive variants where an additional unit is added after each loss for larger profits.</li>
              <li><strong>Monte Carlo Simulation</strong>: Test thousands of betting sessions with realistic probability distributions and random seed sampling.</li>
              <li><strong>Multi-Format Odds Conversion</strong>: Seamlessly convert between Decimal (2.00), Fractional (1/1), American (+100), and Implied Probability (50%).</li>
            </ul>
            
            <div className="cta-section">
              <h3>Ready to Test Your Roulette & Betting Strategy?</h3>
              <p>
                Use the configuration tab above to model your initial bet, bankroll, and odds. Always gamble responsibly and never wager money you cannot afford to lose.
              </p>
              <div className="promotional-section">
                <a href="https://stake.bet/?c=2uo9ldlS" target="_blank" rel="nofollow sponsored noopener noreferrer">
                  <img
                    src={`${process.env.PUBLIC_URL}/martigale strategy calculater.webp`}
                    alt="Martingale Strategy Calculator - Stake Casino"
                    className="promotional-image"
                    loading="lazy"
                    width="250"
                    height="150"
                  />
                </a>
                <p className="promotional-text">
                  <strong>Recommended Platform:</strong> Practice your betting systems at{' '}
                  <a href="https://stake.bet/?c=2uo9ldlS" target="_blank" rel="nofollow sponsored noopener noreferrer">
                    Stake Casino
                  </a>
                </p>
              </div>
            </div>
          </article>
        </section>

        {/* FAQ Section */}
        <section className="faq-section">
          <div className="content-section">
            <h2>Frequently Asked Questions (FAQ)</h2>
            
            {[
              {
                question: "Is the Martingale system safe?",
                answer: "The Martingale betting strategy carries significant financial risk. While it produces frequent small wins in the short run, exponential bet escalation means a rare streak of consecutive losses will completely deplete your bankroll or exceed table limits. Our Martingale calculator helps you visualize these critical risk factors."
              },
              {
                question: "What is the best Martingale strategy for roulette?",
                answer: "The safest way to apply Martingale in roulette is on European (single zero) wheels with even-money bets (Red/Black, Even/Odd). Keeping your base bet to 0.5% - 1% of your total bankroll allows you to survive streaks of 7 to 8 consecutive losses before running out of funds."
              },
              {
                question: "How does the Martingale calculator work?",
                answer: "Our Martingale betting calculator takes your base bet, total bankroll, game odds, and multiplier to generate a complete round-by-round progression table. It calculates required stakes, cumulative loss amounts, payout per win, and exact mathematical probabilities of hitting consecutive loss streaks."
              },
              {
                question: "Can I use this for roulette strategies?",
                answer: "Yes! The Martingale strategy is most commonly played in roulette. Our tool accounts for standard roulette odds and allows you to test both standard Martingale (2x doubling) and Grand Martingale (2x + 1 unit) progressions."
              },
              {
                question: "What bankroll do I need for Martingale?",
                answer: "Your required bankroll is determined by your initial stake and target safety margin. For a $10 base bet, surviving 6 consecutive losses requires $630 ($10+$20+$40+$80+$160+$320), while surviving 8 losses requires $2,550. Use our calculator table to check the exact requirement for your bet size."
              }
            ].map((faq, index) => (
              <div key={index} className="faq-item">
                <button
                  className="faq-question"
                  onClick={() => toggleFaq(index)}
                  aria-expanded={expandedFaqs[index] || false}
                  type="button"
                >
                  <h3>{faq.question}</h3>
                  <span className="faq-toggle">
                    {expandedFaqs[index] ? '−' : '+'}
                  </span>
                </button>
                {expandedFaqs[index] && (
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default App;
